import { SocketClient } from "cbcore-ts"
import {
    IF,
    IS_LIKE_NULL,
    IS_NOT_LIKE_NULL,
    nil,
    NO,
    UIRoute,
    UITextField,
    UITextView,
    UIView,
    UIViewController,
    YES
} from "uicore-ts"
import { CBDataViewCellDescriptor } from "./Custom components/CB/CBDataView"
import { CellView } from "./Custom components/CellView"
import { RHButton } from "./Custom components/RH/RHButton"
import { RHDataView } from "./Custom components/RH/RHDataView"
import { RHLabeledSearchableDropdown } from "./Custom components/RH/RHLabeledSearchableDropdown"
import { RHTextField } from "./Custom components/RH/RHTextField"
import { StatisticsPageDesignTokens } from "./Custom components/RH/Utils_slop/StatisticsPageDesignTokens"
import { StatisticsPageViewStyling } from "./Custom components/RH/Utils_slop/StatisticsPageViewStyling"
import { LanguageService } from "./LanguageService"
import { currencyFormatter, numberFormatter, percentFormatter } from "./RHRStatisticsViewController"
import { RHButtons, RHFont, RHForms, RHSpacing } from "./Custom components/RH/Utils_slop/RHDesignTokens"


// Define a common interface for data items
interface StatisticsComparisonDataItem {
    entityName: string; // Holds procurerName or offererName
    parameterValue: number;
    contractCount: number;
    averagePerContract?: number;
}

const range = (start: number, end: number) => Array.from({ length: (end - start) }, (v, k) => k + start)


export class RHRStatisticsComparisonViewController extends UIViewController {


    entityType: "procurer" | "offerer" = "procurer"

    get titleKey(): string {

        const result = "" + this.class.name + "Title_" + (this.entityType ?? "procurer")
        return result

    }

    explanationLabel: UITextView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        // Text will be set dynamically
        textAlignment: UITextView.textAlignment.center,
        isSingleLine: NO,
        textColor: StatisticsPageDesignTokens.explanationTextColor,
        style: {
            fontFamily: RHFont.familyBase,
            fontSize: RHFont.sizeXSmall,
            fontWeight: StatisticsPageDesignTokens.explanationFontWeight,
            lineHeight: RHFont.lineHeightBase
        }
    })

    yearDropdown = new RHLabeledSearchableDropdown<string>().configuredWithObject({
        label: { text: "Aasta" },
        dropdown: {
            data: range(2023, new Date().getFullYear() + 1).map(year => {
                    return {

                        _id: year + "",
                        title: { en: year + "", est: year + "" },
                        isADropdownDataSection: NO,
                        isADropdownDataRow: YES,

                        attachedObject: year + "",

                        itemCode: year + "",
                        dropdownCode: "Years"

                    }
                }
            ),
            dialogContainerView: this.view,
            isSingleSelection: YES,

            controlEventTargetAccumulator: {
                SelectionDidChange: () => {
                    if (this._ignoreHandleRoute) {
                        return
                    }

                    // Only update the route, handleRoute will trigger content update if needed
                    if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                        UIRoute.currentRoute.routeBySettingParameterInComponent(
                            RHRStatisticsComparisonViewController.routeComponentName,
                            RHRStatisticsComparisonViewController.ParameterIdentifierName.year,
                            this.yearDropdown.selectedItemCodes.firstElement
                        ).apply()
                    }
                }
            }
        }
    }).addedAsSubviewToView(this.view)

    procurementProcedureCodeDropdown = new RHLabeledSearchableDropdown<string>()
        .configuredWithObject({
            label: { text: "Menetlusliik" },
            dropdown: {
                data: [], // Data loaded dynamically
                dialogContainerView: this.view,
                isSingleSelection: NO, // Allow multiple selections
                controlEventTargetAccumulator: {
                    SelectionDidChange: () => {
                        if (this._ignoreHandleRoute) {
                            return
                        }

                        if (this.routeComponent) {
                            const route = UIRoute.currentRoute
                                .routeBySettingParameterInComponent(
                                    RHRStatisticsComparisonViewController.routeComponentName,
                                    RHRStatisticsComparisonViewController.ParameterIdentifierName.procType, // Use new
                                                                                                            // param
                                                                                                            // name
                                    this.procurementProcedureCodeDropdown.selectedItemCodes.join("+") ?? "" // Join
                                                                                                            // selected
                                                                                                            // codes
                                )

                            // Decide whether to replace or push history based on if the param already exists
                            if (UIRoute.currentRoute.componentWithViewController(
                                this.class)?.parameters[RHRStatisticsComparisonViewController.ParameterIdentifierName.procType]) {
                                route.apply()
                            }
                            else {
                                route.applyByReplacingCurrentRouteInHistory()
                            }
                        }
                    }
                }
            },
            enabled: NO
        }).addedAsSubviewToView(this.view)

    comparisonParameterDropdown = new RHLabeledSearchableDropdown<{
        name: string
        type: string
    }>().configuredWithObject({
        label: { text: "Võrdlusparameeter" },
        dropdown: {
            dialogContainerView: this.view,
            isSingleSelection: YES,

            controlEventTargetAccumulator: {
                SelectionDidChange: () => {
                    if (this._ignoreHandleRoute) {
                        return
                    }

                    // Only update the route, handleRoute will trigger content update if needed
                    if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                        UIRoute.currentRoute.routeBySettingParameterInComponent(
                            RHRStatisticsComparisonViewController.routeComponentName,
                            RHRStatisticsComparisonViewController.ParameterIdentifierName.parameter,
                            this.comparisonParameterDropdown.dropdown.selectedItemCodes.firstElement ?? nil
                        ).apply()
                    }
                }
            }
        }
    }).addedAsSubviewToView(this.view)


    filterFromLabel: UITextView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        text: "Alates",
        textAlignment: UITextView.textAlignment.left,
        textColor: RHForms.dropdownLabelTextColor,
        style: {
            fontSize: RHForms.dropdownLabelFontSize,
            fontWeight: RHForms.dropdownLabelFontWeight,
            lineHeight: RHForms.dropdownLabelLineHeight
        }
    })

    filterFromValueField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({

        viewHTMLElement: {
            placeholder: "Sisesta väärtus filtreerimiseks",
            type: "number"  // Use number input type for better mobile experience
        },
        text: "1",
        type: "number",
        style: {
            fontSize: RHFont.sizeSmall
        },
        controlEventTargetAccumulator: {
            TextChange: () => {
                if (UIRoute.currentRoute.componentWithViewController(this.class)) {

                    this._ignoreNextRouteChange = YES

                    UIRoute.currentRoute.routeBySettingParameterInComponent(
                        RHRStatisticsComparisonViewController.routeComponentName,
                        RHRStatisticsComparisonViewController.ParameterIdentifierName.filterFromValue,
                        this.filterFromValueField.text
                    ).applyByReplacingCurrentRouteInHistory()

                    this.applyFilterToDataView(
                        IF(this.filterFromValueField.text.length)(
                            () => this.filterFromValueField.text.numericalValue
                        )(),
                        IF(this.filterUpToValueField.text.length)(
                            () => this.filterUpToValueField.text.numericalValue
                        )()
                    )

                }
            }
        }


    })

    filterUpToLabel: UITextView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        text: "Kuni",
        textAlignment: UITextView.textAlignment.left,
        textColor: RHForms.dropdownLabelTextColor,
        style: {
            fontSize: RHForms.dropdownLabelFontSize,
            fontWeight: RHForms.dropdownLabelFontWeight,
            lineHeight: RHForms.dropdownLabelLineHeight
        }
    })

    filterUpToValueField = new RHTextField().addedAsSubviewToView(this.view).configuredWithObject({

        viewHTMLElement: {
            placeholder: "Sisesta väärtus filtreerimiseks",
            type: "number"  // Use number input type for better mobile experience
        },
        text: "1",
        type: "number",
        style: {
            fontSize: RHFont.sizeSmall
        },
        controlEventTargetAccumulator: {
            TextChange: () => {
                if (UIRoute.currentRoute.componentWithViewController(this.class)) {

                    this._ignoreNextRouteChange = YES

                    UIRoute.currentRoute.routeBySettingParameterInComponent(
                        RHRStatisticsComparisonViewController.routeComponentName,
                        RHRStatisticsComparisonViewController.ParameterIdentifierName.filterUpToValue,
                        this.filterUpToValueField.text
                    ).applyByReplacingCurrentRouteInHistory()

                    this.applyFilterToDataView(
                        IF(this.filterFromValueField.text.length)(
                            () => this.filterFromValueField.text.numericalValue
                        )(),
                        IF(this.filterUpToValueField.text.length)(
                            () => this.filterUpToValueField.text.numericalValue
                        )()
                    )

                }
            }
        }


    })

    clearFilterButton = new RHButton().addedAsSubviewToView(this.view).configuredWithObject({
        titleLabel: {
            text: "Eemalda filter"
        },
        variant: "light",
        contentPadding: RHButtons.compactContentPadding,
        style: {
            fontSize: RHFont.sizeSmall
        },
        // type: "",
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: (sender, event) => {
                this.clearFilter()
            }
        }
    })

    statisticsView = new RHDataView<StatisticsComparisonDataItem>().configuredWithObject({
        searchTextField: {
            placeholderText: LanguageService.stringForCurrentLanguage({
                    en: "Search by keywords (comma separated)",
                    est: "Otsi märksõnade järgi (komadega eraldatud)"
                }
            )
        }
    }).addedAsSubviewToView(this.view)
    bottomView = new UIView().addedAsSubviewToView(this.view)

    // Filter properties


    allData: StatisticsComparisonDataItem[] = []
    _isInitialized = NO
    _ignoreNextRouteChange = NO
    _ignoreHandleRoute = NO

    get isInitialized(): boolean {
        return this._isInitialized
    }

    set isInitialized(isInitialized: boolean) {

        this._isInitialized = isInitialized
        // this.view.alpha = IF(isInitialized)(RETURNER(1)).ELSE(RETURNER(0.5))

        if (!isInitialized) {
            this.statisticsView.data = []
            this.statisticsView.descriptors = []
            this.statisticsView.noDataMessage = "Server valmistab statistikat ette"

        }

    }


    constructor(view: UIView) {

        super(view)

        this.view.configureWithObject({
            backgroundColor: StatisticsPageDesignTokens.pageBackgroundColor
        })
        this.statisticsView.noDataMessage = StatisticsPageDesignTokens.emptyStateMessageComparison

        this.isInitialized = NO

        // Initial content update will be triggered by the first handleRoute call
        // via the hashchange event after the app loads.
        // If immediate update is needed before hashchange, call handleRoute explicitly:
        // this.handleRoute(UIRoute.currentRoute); // Or call updateContent directly if route isn't ready

    }

    private updateUITextsBasedOnEntityType() {
        if (this.entityType === "procurer") {
            this.explanationLabel.text = "Siin on võimalik võrrelda erinevate hankijate statistikat valitud aastal."
        }
        else {
            this.explanationLabel.text = "Siin on võimalik võrrelda erinevate pakkujate statistikat valitud aastal."
        }
        this.view.setNeedsLayout() // Ensure layout updates if text changes size
    }


    get selectedComparisonParameterName(): string {

        // Return the first selected parameter or default to contractMonetarySum
        return UIRoute.currentRoute.componentWithViewController(
            this.class)?.parameters.parameter ?? "contractMonetarySum"

    }


    private clearFilter() {

        // Clear the filter value field
        this.filterFromValueField.text = ""
        this.filterUpToValueField.text = ""

        // Reset the operator to default
        // this.filterOperatorDropdown.selectedItemCodes = [">="]

        this.filterFromValueField.sendControlEventForKey(UITextField.controlEvent.TextChange)

    }


    private applyFilterToDataView(filterFromValue: number, filterUpToValue: number) {

        if (IS_LIKE_NULL(filterFromValue) && IS_LIKE_NULL(filterUpToValue)) {
            // No valid filter value, show all data
            this.statisticsView.data = this.allData
            this.statisticsView.noDataMessage = IF(this.allData.length)(
                () => ""
            ).ELSE(() =>
                StatisticsPageDesignTokens.emptyStateMessageComparison
            )
            return
        }

        // Apply filter based on the selected operator
        const filteredData = this.allData.filter(item => {

            const value = item.parameterValue

            if (IS_NOT_LIKE_NULL(filterFromValue) && filterFromValue > value) {
                return NO
            }

            if (IS_NOT_LIKE_NULL(filterUpToValue) && filterUpToValue < value) {
                return NO
            }

            return YES

        })

        this.statisticsView.data = filteredData
        this.statisticsView.noDataMessage = IF(filteredData.length)(
            () => ""
        ).ELSE(() =>
            "Valitud filtritega võrreldavaid tulemusi ei leitud."
        )
    }


    private async retrieveParameterNames() {

        try {

            const response = await SocketClient.RetrieveStatisticsParameterNames({
                entityType: this.entityType
            })

            const comparisonParameters = response.result

            this.comparisonParameterDropdown.dropdown.data = comparisonParameters.map(parameterObject => {
                return {
                    _id: parameterObject.name,
                    title: LanguageService.localizedTextObjectForKey(
                        parameterObject.name,
                        parameterObject.name
                    ),
                    isADropdownDataSection: NO,
                    isADropdownDataRow: YES,

                    attachedObject: parameterObject,

                    itemCode: parameterObject.name,
                    dropdownCode: "Parameters"
                }
            })

            const previousIgnoreHandleRoute = this._ignoreHandleRoute
            this._ignoreHandleRoute = YES
            this.comparisonParameterDropdown.dropdown.selectedItemCodes = [this.selectedComparisonParameterName]
            this._ignoreHandleRoute = previousIgnoreHandleRoute

        }
        catch (error) {

            console.error("Error fetching parameter names:", error)

        }

    }

    private async retrieveProcurementTypesAndInitDropdown() {

        try {

            const procedureCodes = (await SocketClient.RetrieveProcurementProcedureCodes()).result
            const data = Object.entries(procedureCodes).map(([code, name]) => ({
                _id: code,
                title: LanguageService.localizedTextObjectForKey(
                    code + "_procurement_type",
                    name
                ),
                isADropdownDataSection: NO,
                isADropdownDataRow: YES,
                attachedObject: code,
                itemCode: code,
                dropdownCode: "ProcurementTypes"
            }))
            this.procurementProcedureCodeDropdown.dropdown.data = data

            const routeProcurementTypes = UIRoute.currentRoute
                .componentWithViewController(this.class)
                ?.parameters[RHRStatisticsComparisonViewController.ParameterIdentifierName.procType]
            let selectedProcurementTypes = Object.keys(procedureCodes)
            if (routeProcurementTypes !== undefined) {
                selectedProcurementTypes = decodeURIComponent(routeProcurementTypes).split("+").filter(p => p)
            }

            const previousIgnoreHandleRoute = this._ignoreHandleRoute
            this._ignoreHandleRoute = YES
            this.procurementProcedureCodeDropdown.selectedItemCodes = selectedProcurementTypes
            this._ignoreHandleRoute = previousIgnoreHandleRoute


            // const procurementTypes =
            // UIRoute.currentRoute.componentWithViewController(this.class)?.parameters[RHRStatisticsViewController.ParameterIdentifierName.procType]
            // UIRoute.currentRoute.routeBySettingParameterInComponent( RHRStatisticsViewController.routeComponentName,
            // RHRStatisticsViewController.ParameterIdentifierName.procType, procurementTypes ||
            // procedureCodes.join("+") ).applyByReplacingCurrentRouteInHistory()

            this.procurementProcedureCodeDropdown.enabled = YES

        }
        catch (error) {
            console.error("Error fetching procurement types:", error)
        }

    }


    // Renamed updateStatisticsView to fetchAndDisplayStatisticsData
    private async fetchAndDisplayStatisticsData(
        entityType: "procurer" | "offerer",
        selectedYear: string,
        comparisonParameterName: string,
        procurementProcedureCodes: string[]
    ) {

        if (!this.yearDropdown.dropdown.selectedItemCodes.length) {
            this.statisticsView.data = []
            this.statisticsView.noDataMessage = StatisticsPageDesignTokens.emptyStateMessageComparison
            return
        }

        this.statisticsView.isSearchAvailable = YES

        if (!this.statisticsView.data.length) {

            this.statisticsView.enabled = NO

        }

        // console.trace("About to call the big expensive function")
        // console.log(UIRoute.currentRoute.stringRepresentation)
        // Use the consolidated endpoint
        const response = await SocketClient.StatisticsComparison({
            entityType,
            year: selectedYear,
            parameterName: comparisonParameterName,
            procurementProcedureCodes: procurementProcedureCodes
        })

        // Get name and type for the selected comparison parameter
        const selectedComparisonParameterObject = this.comparisonParameterDropdown.dropdown.data.find(
            p => p.attachedObject.name === this.selectedComparisonParameterName)?.attachedObject ?? {
            name: this.selectedComparisonParameterName,
            type: "count"
        }

        // Configure column descriptors dynamically
        let descriptors: CBDataViewCellDescriptor<StatisticsComparisonDataItem>[] = [
            {
                keyPath: "entityName", // Use the common keyPath
                title: IF(entityType === "procurer")(() => "Hankija").ELSE(() => "Pakkuja"), // Dynamic title
                allowSorting: NO, // Usually names are not sorted by default, but could be YES
                updateCell: (
                    cellView: CellView,
                    dataItem: StatisticsComparisonDataItem
                ) => {
                    StatisticsPageViewStyling.applyTableCellChrome(cellView)
                    cellView.titleLabel!.text = dataItem.entityName
                }
            },
            {
                keyPath: "parameterValue",
                title: LanguageService.stringForKey(
                    selectedComparisonParameterObject.name,
                    LanguageService.currentLanguageKey,
                    selectedComparisonParameterObject.name
                ),
                allowSorting: YES,
                dataType: "number",
                updateCell: (cellView: CellView, dataItem: StatisticsComparisonDataItem) => {
                    StatisticsPageViewStyling.applyTableCellChrome(cellView)
                    let value = dataItem.parameterValue

                    // Format based on parameter type from server metadata
                    if (selectedComparisonParameterObject.type === "monetary") {
                        cellView.titleLabel!.text = currencyFormatter.format(value)
                    }
                    else if (selectedComparisonParameterObject.type === "percentage") {
                        cellView.titleLabel!.text = percentFormatter.format(value)
                    }
                    else if (selectedComparisonParameterObject.type === "average") {
                        cellView.titleLabel!.text = numberFormatter.format(value)
                    }
                    else {
                        // Handle other non-numeric string values if necessary
                        cellView.titleLabel!.text = String(value)
                    }
                }
            }
        ]

        // Add contract count column if the selected parameter isn't the contract count itself
        const contractCountParamName: string = IF(entityType === "procurer")(() =>
            "numberOfContractsForTheYear"
        ).ELSE(() =>
            "numberOfContractsWonForTheYear"
        )

        if (selectedComparisonParameterObject.name !== contractCountParamName) {
            descriptors.push({
                keyPath: "contractCount",
                title: IF(entityType === "procurer")(() => "Lepingute arv").ELSE(() => "Võidetud lepingute arv"),
                allowSorting: YES,
                dataType: "number",
                updateCell: (
                    cellView: CellView,
                    dataItem: StatisticsComparisonDataItem
                ) => {
                    StatisticsPageViewStyling.applyTableCellChrome(cellView)
                    cellView.titleLabel!.text = dataItem.contractCount.toString()
                }
            })
        }


        // Add average per contract for monetary parameters
        if (selectedComparisonParameterObject.type === "monetary") {
            descriptors.push({
                keyPath: "averagePerContract",
                title: "Keskmine maksumus (€)",
                allowSorting: YES,
                dataType: "number",
                updateCell: (cellView: CellView, dataItem: StatisticsComparisonDataItem) => {
                    StatisticsPageViewStyling.applyTableCellChrome(cellView)
                    if (dataItem.averagePerContract !== undefined && isFinite(dataItem.averagePerContract)) {
                        cellView.titleLabel!.text = currencyFormatter.format(dataItem.averagePerContract)
                    }
                    else {
                        cellView.titleLabel!.text = "–"
                    }
                }
            })
        }

        this.statisticsView.descriptors = descriptors
        const comparisonTableColumnWeights = descriptors.map((_descriptor, index) =>
            IF(index == 0)(() =>
                440
            ).ELSE(() =>
                220
            )
        )
        this.statisticsView.tableHeaderView.cellWeights = comparisonTableColumnWeights
        this.statisticsView.tableView.cellWeights = comparisonTableColumnWeights
        this.statisticsView.hasResizableColumns = YES

        // Map backend data to the common StatisticsComparisonDataItem interface
        this.allData = response.result.data.map((item): StatisticsComparisonDataItem => {

            return {
                entityName: item.entityName,
                parameterValue: item.parameterValue,
                contractCount: item.contractCount, // Assumes backend provides this consistently
                averagePerContract: item.averagePerContract
            }

        })

        this.statisticsView.enabled = YES


    }


    static override readonly routeComponentName = "comparestatistics"

    static override readonly ParameterIdentifierName = {
        "entityType": "entityType",
        "year": "year",
        "parameter": "parameter",
        "procType": "procType",
        "filterFromValue": "filterFromValue",
        "filterUpToValue": "filterUpToValue"
    }

    override async viewWillAppear() {

        await super.viewWillAppear()

        this.view.loading = YES
        this.view.setNeedsLayoutUpToRootView()
        UIView.layoutViewsIfNeeded()
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))

        try {
            const entityTypeFromRoute = this.routeComponent?.parameters?.entityType
            if (entityTypeFromRoute === "procurer" || entityTypeFromRoute === "offerer") {
                this.entityType = entityTypeFromRoute
            }

            this.isInitialized = this.isInitialized

            this.updateUITextsBasedOnEntityType()
            await this.retrieveParameterNames()
            await this.retrieveProcurementTypesAndInitDropdown()
            const previousIgnoreHandleRoute = this._ignoreHandleRoute
            this._ignoreHandleRoute = YES
            this.comparisonParameterDropdown.selectedItemCodes = [this.selectedComparisonParameterName]
            this._ignoreHandleRoute = previousIgnoreHandleRoute

            console.log("Statistics comparison view controller finished initializing")

            this.isInitialized = YES
            this.view.loading = NO
            this.view.setNeedsLayoutUpToRootView()
            await this.handleRoute(UIRoute.currentRoute)
        }
        finally {
            this.view.loading = NO
            this.view.setNeedsLayoutUpToRootView()
        }

    }

    override async viewDidAppear() {

    }

    override async viewWillDisappear() {

        this.isInitialized = NO
    }

    override async handleRoute(route: UIRoute) {

        super.handleRoute(route)

        if (!this.routeComponent || this._ignoreHandleRoute) {
            return
        }

        const entityTypeFromRoute = this.routeComponent?.parameters?.entityType
        if ((entityTypeFromRoute === "procurer" || entityTypeFromRoute === "offerer") &&
            entityTypeFromRoute != this.entityType) {

            this._ignoreHandleRoute = YES
            this.entityType = entityTypeFromRoute
            this.updateUITextsBasedOnEntityType()
            if (this.isInitialized) {
                await this.retrieveParameterNames()
                await this.retrieveProcurementTypesAndInitDropdown()
                this.isInitialized = YES
            }

            this._ignoreHandleRoute = NO

        }

        const currentYear = decodeURIComponent(this.routeComponent?.parameters?.year || new Date().getFullYear()
            .toString())
        const currentComparisonParameterName = decodeURIComponent(
            this.routeComponent?.parameters?.parameter || "contractMonetarySum")
        const routeProcurementTypes = this.routeComponent?.parameters?.procType
        let currentProcurementProcedureCodes = this.procurementProcedureCodeDropdown.selectedItemCodes
        if (routeProcurementTypes !== undefined) {
            currentProcurementProcedureCodes = decodeURIComponent(routeProcurementTypes).split("+").filter(p => p)
        }

        const currentFilterFromValue = decodeURIComponent(this.routeComponent?.parameters?.filterFromValue || "")
        const currentFilterUpToValue = decodeURIComponent(this.routeComponent?.parameters?.filterUpToValue || "")

        this._ignoreHandleRoute = YES

        // --- Update UI elements directly related to route parameters ---
        this.yearDropdown.dropdown.selectedItemCodes = [currentYear]
        this.comparisonParameterDropdown.selectedItemCodes = [currentComparisonParameterName]
        this.procurementProcedureCodeDropdown.selectedItemCodes = currentProcurementProcedureCodes
        this.filterFromValueField.text = currentFilterFromValue
        this.filterUpToValueField.text = currentFilterUpToValue

        this._ignoreHandleRoute = NO


        if (this.isInitialized && !this._ignoreNextRouteChange) {

            if (!currentProcurementProcedureCodes.join("+") || !currentComparisonParameterName || !currentYear) {

                this.statisticsView.data = []
                this.statisticsView.noDataMessage = StatisticsPageDesignTokens.emptyStateMessageComparison

            }
            else {

                this.statisticsView.data = []
                this.statisticsView.noDataMessage = "Laen andmeid"
                this.statisticsView.loading = YES
                this.view.setNeedsLayoutUpToRootView()
                UIView.layoutViewsIfNeeded()
                await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))

                try {
                    await this.fetchAndDisplayStatisticsData(
                        this.entityType,
                        currentYear,
                        currentComparisonParameterName,
                        currentProcurementProcedureCodes
                    )
                    this.applyFilterToDataView(
                        IF(this.filterFromValueField.text.length)(
                            () => this.filterFromValueField.text.numericalValue
                        )(),
                        IF(this.filterUpToValueField.text.length)(
                            () => this.filterUpToValueField.text.numericalValue
                        )()
                    )
                }
                finally {
                    this.view.setNeedsLayoutUpToRootView()
                    await StatisticsPageViewStyling.waitForLoadingCompletionFrameBudget()
                    this.statisticsView.loading = NO
                }

                this.view.setNeedsLayoutUpToRootView()

            }

        }
        else {

            this._ignoreNextRouteChange = NO

        }


    }

    override updateViewConstraints() {

        super.updateViewConstraints()

    }

    override updateViewStyles() {

        super.updateViewStyles()

    }

    override viewDidLayoutSubviews() {

        super.viewDidLayoutSubviews()

    }


    override layoutViewSubviews() {

        super.layoutViewSubviews()

        const bounds = this.view.contentBoundsWithInset(RHSpacing.large)
            .rectangleWithMaxWidth(StatisticsPageDesignTokens.wideContentMaxWidth, 0.5)
        const controlGap = StatisticsPageDesignTokens.filterControlGap
        const preferredDropdownWidths = [
            160,
            480,
            360
        ]
        const dropdownWidths = IF((preferredDropdownWidths.summedValue + controlGap * 2) <= bounds.width)(() =>
            preferredDropdownWidths
        ).ELSE(() => {
            const availableWidth = bounds.width - controlGap * 2
            return [
                availableWidth * 0.22,
                availableWidth * 0.42,
                availableWidth * 0.36
            ]
        })
        const dropdownHeight = [
            this.yearDropdown.intrinsicContentHeight(dropdownWidths[0]),
            this.procurementProcedureCodeDropdown.intrinsicContentHeight(dropdownWidths[1]),
            this.comparisonParameterDropdown.intrinsicContentHeight(dropdownWidths[2]),
            StatisticsPageDesignTokens.dropdownMinHeight
        ].max()
        const explanationHeight = [
            this.explanationLabel.intrinsicContentHeight(bounds.width),
            StatisticsPageDesignTokens.explanationMinHeight
        ].max()
        const labeledFieldHeight = RHForms.dropdownLabelHeight +
            RHForms.dropdownLabelControlGap +
            RHForms.dropdownControlHeight
        const inlineFilterLayout = bounds.width >= 1480
        const firstFilterRowHeight = IF(inlineFilterLayout)(() => labeledFieldHeight).ELSE(() => dropdownHeight)
        const rangeFilterWidths = [
            StatisticsPageDesignTokens.comparisonFilterFieldWidth,
            StatisticsPageDesignTokens.comparisonFilterFieldWidth,
            116
        ]
        const rangeFilterWidth = rangeFilterWidths.summedValue + controlGap * 2

        const assignLabeledFieldFrames = (
            label: UITextView,
            field: RHTextField,
            itemFrame: typeof bounds
        ) => {
            itemFrame
                .rectangleWithHeight(RHForms.dropdownLabelHeight)
                .assignedAsFrameOfView(label)
                .rectangleForNextRow(
                    RHForms.dropdownLabelControlGap,
                    RHForms.dropdownControlHeight
                )
                .assignedAsFrameOfView(field)
        }

        const assignClearButtonFrame = (itemFrame: typeof bounds) => {
            itemFrame
                .rectangleWithInsets(
                    0,
                    0,
                    0,
                    RHForms.dropdownLabelHeight +
                    RHForms.dropdownLabelControlGap
                )
                .rectangleWithHeight(34, 0.5)
                .assignedAsFrameOfView(this.clearFilterButton)
        }

        const lastFilterFrame = bounds
            .rectangleWithHeight(explanationHeight)
            .rectangleWithWidth([bounds.width, StatisticsPageDesignTokens.explanationMaxWidth].min(), 0.5)
            .assignedAsFrameOfView(this.explanationLabel)
            .rectangleForNextRow(StatisticsPageDesignTokens.explanationBottomGap, firstFilterRowHeight)
            .performFunctionWithSelf(firstFilterRowFrame =>
                IF(inlineFilterLayout)(() => {
                    const inlineWidths = [
                        ...preferredDropdownWidths,
                        ...rangeFilterWidths
                    ]

                    return firstFilterRowFrame
                        .rectangleWithWidth([
                            bounds.width,
                            1420,
                            inlineWidths.summedValue + controlGap * (inlineWidths.length - 1)
                        ].min(), 0.5)
                        .performingFunctionWithSelf(inlineFrame => {
                            const inlineFrames = inlineFrame.framesByDistributingViewsAsRow(
                                [
                                    this.yearDropdown,
                                    this.procurementProcedureCodeDropdown,
                                    this.comparisonParameterDropdown,
                                    this.filterFromValueField,
                                    this.filterUpToValueField,
                                    this.clearFilterButton
                                ],
                                controlGap,
                                inlineWidths
                            )

                            assignLabeledFieldFrames(this.filterFromLabel, this.filterFromValueField, inlineFrames[3])
                            assignLabeledFieldFrames(this.filterUpToLabel, this.filterUpToValueField, inlineFrames[4])
                            assignClearButtonFrame(inlineFrames[5])
                        })
                }).ELSE(() =>
                    firstFilterRowFrame
                        .rectangleWithWidth([
                            bounds.width,
                            dropdownWidths.summedValue + controlGap * (dropdownWidths.length - 1)
                        ].min(), 0.5)
                        .performingFunctionWithSelf(controlsFrame => controlsFrame.distributeViewsAlongWidth(
                            [
                                this.yearDropdown,
                                this.procurementProcedureCodeDropdown,
                                this.comparisonParameterDropdown
                            ],
                            undefined,
                            controlGap,
                            dropdownWidths
                        ))
                        .rectangleForNextRow((RHSpacing.small + RHSpacing.tiny), labeledFieldHeight)
                        .rectangleWithWidth(rangeFilterWidth)
                        .rectangleWithX([
                            bounds.x,
                            [
                                this.comparisonParameterDropdown.frame.x,
                                bounds.max.x - rangeFilterWidth
                            ].min()
                        ].max())
                        .performingFunctionWithSelf(rangeFrame => {
                            const rangeFrames = rangeFrame.framesByDistributingViewsAsRow(
                                [this.filterFromValueField, this.filterUpToValueField, this.clearFilterButton],
                                controlGap,
                                rangeFilterWidths
                            )

                            assignLabeledFieldFrames(this.filterFromLabel, this.filterFromValueField, rangeFrames[0])
                            assignLabeledFieldFrames(this.filterUpToLabel, this.filterUpToValueField, rangeFrames[1])
                            assignClearButtonFrame(rangeFrames[2])
                        })
                )
            )

        const statisticsViewHeight = [
            [
                this.statisticsView.intrinsicContentHeight(bounds.width),
                520
            ].max(),
            840
        ].min()

        lastFilterFrame
            .rectangleWithWidth(bounds.width)
            .rectangleWithX(bounds.x)
            .rectangleForNextRow(
                RHSpacing.large,
                statisticsViewHeight
            )
            .assignedAsFrameOfView(this.statisticsView)
            .rectangleForNextRow(
                RHSpacing.large,
                RHSpacing.xLarge
            )
            .assignedAsFrameOfView(this.bottomView)
    }

}
